import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { Box, Typography, Paper, Link, Container, Grid } from '@material-ui/core';
import { Help as HelpIcon } from '@material-ui/icons';
import { WizardStepContainer } from './partials';
import { useLocalizedMessage } from '../../../../../util/useLocalizedMessage';
import ConnectivityCheckForm from '../../../connectivity/ConnectivityCheckForm';
import InfoBlock from '../../../../../features/integration-wizard/components/common/InfoBlock';
import IntlMessages from 'util/IntlMessages';

export default function WizardConnectivityCheckStep({ onReady, onHasChanges, crawlerApi }) {
  const localizeMessage = useLocalizedMessage();
  const { connectivityStatus } = useSelector((s) => s.wizard);

  useEffect(() => {
    if (onReady) {
      // Always enable Next button when check is complete (success or error)
      if (connectivityStatus === 'success' || connectivityStatus === 'error') {
        onReady(true);
      } else {
        onReady(false);
      }
    }
  }, [connectivityStatus, onReady]);

  return (
    <div className={classNames('wizard-step', 'wizard-step-connectivity')}>
      <WizardStepContainer title={localizeMessage('wizard.connectivity.title')}>
        <div className="row">
          <div className="col-8 offset-2">
            <p className="sub-header" style={{ textAlign: 'center' }}>
              {localizeMessage('wizard.connectivity.subheader')}
            </p>
          </div>
        </div>

        <Container maxWidth="lg" style={{ height: '100%', paddingTop: 16, paddingBottom: 32 }}>
          <Grid container spacing={4} justify="center" style={{ height: '100%' }}>
            {/* Settings Panel */}
            <Grid item xs={12} md={7} style={{ height: '100%' }}>
              <Paper elevation={1} style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <Box p={3} display="flex" flexDirection="column" height="100%">
                  <Box flex={1} overflow="auto">
                    <Box mb={4}>
                      <InfoBlock
                        title={localizeMessage('wizard.connectivity.ngrok.title')}
                        description={localizeMessage('wizard.connectivity.ngrok.description')}
                      />

                      <ConnectivityCheckForm
                        autoCheck
                        showTitle={false}
                        onHasChanges={onHasChanges}
                        crawlerApi={crawlerApi}
                      />
                    </Box>
                  </Box>
                </Box>
              </Paper>
            </Grid>

            {/* Info Panel */}
            <Grid item xs={12} md={5} style={{ height: '100%' }}>
              <Paper elevation={0} style={{ height: '100%', padding: 24, backgroundColor: '#f5f7f9', overflowY: 'auto' }}>
                <Box textAlign="center" mb={4}>
                  <HelpIcon style={{ fontSize: 64, color: '#90a4ae' }} />
                  <Typography variant="h5" gutterBottom>
                    {localizeMessage('wizard.connectivity.ngrok.title')}
                  </Typography>
                </Box>

                <Typography variant="body1" paragraph>
                  {localizeMessage('wizard.connectivity.ngrok.description')}
                </Typography>

                <Box mt={4} p={2} bgcolor="#fafafa" borderRadius={4} border="1px dashed #ccc">
                  <Typography variant="subtitle2" gutterBottom><IntlMessages id="ui.welcome.wizzard.wizardSteps.wizardConnectivityCheckStep.needMoreHelp" /></Typography>
                  <Link
                    href="https://ngrok.com/docs/what-is-ngrok"
                    target="_blank"
                    rel="noopener"
                    style={{ display: 'block', marginBottom: 8 }}
                  >
                    {localizeMessage('wizard.connectivity.ngrok.learnMore')}
                  </Link>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </WizardStepContainer>
    </div>
  );
}

WizardConnectivityCheckStep.propTypes = {
  onReady: PropTypes.func,
  onHasChanges: PropTypes.func,
  crawlerApi: PropTypes.shape({
    check: PropTypes.func.isRequired,
    save: PropTypes.func.isRequired,
  }),
};

WizardConnectivityCheckStep.defaultProps = {
  onReady: null,
  onHasChanges: null,
  crawlerApi: null,
};
